import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

interface HighlightItem {
  id: string;
  type: 'movie' | 'watchlist';
  timestamp: number;
}

interface HighlightContextType {
  // State
  highlightedItem: HighlightItem | null;

  // Actions
  highlightMovie: (movieId: string) => void;
  highlightWatchlist: (watchlistId: string) => void;
  clearHighlight: () => void;

  // Helpers
  isHighlighted: (id: string, type: 'movie' | 'watchlist') => boolean;
  scrollToAndHighlight: (elementId: string, type: 'movie' | 'watchlist') => void;
}

const HighlightContext = createContext<HighlightContextType | undefined>(undefined);

// Duration for highlight animation (in ms)
const HIGHLIGHT_DURATION = 2000;
// Maximum age of highlight before auto-clearing (in ms)
const HIGHLIGHT_EXPIRY = 10000;

export const HighlightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [highlightedItem, setHighlightedItem] = useState<HighlightItem | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear any existing timeout when setting new highlight
  const clearExistingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Auto-clear highlight after duration
  useEffect(() => {
    if (highlightedItem) {
      clearExistingTimeout();
      timeoutRef.current = setTimeout(() => {
        setHighlightedItem(null);
      }, HIGHLIGHT_DURATION);
    }

    return () => clearExistingTimeout();
  }, [highlightedItem, clearExistingTimeout]);

  const highlightMovie = useCallback((movieId: string) => {
    console.log('🎬 HighlightContext: Highlighting movie:', movieId);
    setHighlightedItem({
      id: movieId,
      type: 'movie',
      timestamp: Date.now()
    });
  }, []);

  const highlightWatchlist = useCallback((watchlistId: string) => {
    console.log('📋 HighlightContext: Highlighting watchlist:', watchlistId);
    setHighlightedItem({
      id: watchlistId,
      type: 'watchlist',
      timestamp: Date.now()
    });
  }, []);

  const clearHighlight = useCallback(() => {
    clearExistingTimeout();
    setHighlightedItem(null);
  }, [clearExistingTimeout]);

  const isHighlighted = useCallback((id: string, type: 'movie' | 'watchlist'): boolean => {
    if (!highlightedItem) return false;

    // Check if highlight has expired
    const age = Date.now() - highlightedItem.timestamp;
    if (age > HIGHLIGHT_EXPIRY) {
      return false;
    }

    return highlightedItem.id === id && highlightedItem.type === type;
  }, [highlightedItem]);

  const scrollToAndHighlight = useCallback((elementId: string, type: 'movie' | 'watchlist') => {
    console.log('📍 HighlightContext: Scrolling to and highlighting:', elementId, type);

    // Set the highlight first
    if (type === 'movie') {
      highlightMovie(elementId);
    } else {
      highlightWatchlist(elementId);
    }

    const selector = type === 'movie'
      ? `[data-movie-id="${elementId}"]`
      : `[data-watchlist-id="${elementId}"]`;

    const started = Date.now();
    const maxMs = 5000;
    const intervalMs = 80;

    const tryScroll = () => {
      const element = document.querySelector(selector);
      if (element) {
        console.log('📍 HighlightContext: Found element, scrolling into view');
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
        return;
      }
      if (Date.now() - started >= maxMs) {
        console.log('📍 HighlightContext: Element not found after retry:', selector);
        return;
      }
      window.setTimeout(tryScroll, intervalMs);
    };

    // First attempt after paint; then retry until DOM catches up (e.g. new watchlist card on native).
    window.requestAnimationFrame(() => window.setTimeout(tryScroll, 0));
  }, [highlightMovie, highlightWatchlist]);

  return (
    <HighlightContext.Provider
      value={{
        highlightedItem,
        highlightMovie,
        highlightWatchlist,
        clearHighlight,
        isHighlighted,
        scrollToAndHighlight
      }}
    >
      {children}
    </HighlightContext.Provider>
  );
};

export const useHighlight = () => {
  const context = useContext(HighlightContext);
  if (context === undefined) {
    throw new Error('useHighlight must be used within a HighlightProvider');
  }
  return context;
};

// Optional hook that doesn't throw if context is missing (for components that may be used outside the provider)
export const useHighlightOptional = () => {
  return useContext(HighlightContext);
};
